import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { JobListing } from '../../types/jobs';
import { BtnDefault } from '../common/Buttons';

interface JobListingWithActions extends JobListing {
  actions?: any;
  status?: string;
}

interface JobListingColumnParams {
  onView: (data: JobListingWithActions) => void;
  onDelete: (data: JobListingWithActions) => void;
}

const JobActionsCell: React.FC<{
  data: JobListingWithActions;
  onView: (data: JobListingWithActions) => void;
  onDelete: (data: JobListingWithActions) => void;
}> = ({ data, onView, onDelete }) => {
  return (
    <div className="flex gap-2 p-1">
      {data?.status !== 'selected' && (
        <BtnDefault onClick={() => onView(data)}>View</BtnDefault>
      )}
      <BtnDefault onClick={() => onDelete(data)}>Delete</BtnDefault>
    </div>
  );
};

export const useJobListingColumns = ({
  onView,
  onDelete,
}: JobListingColumnParams): ColDef<JobListingWithActions>[] => {
  return useMemo(
    () => [
      ...jobListingColumnsDefs,
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        pinned: 'right',
        suppressSizeToFit: true,
        lockPinned: true,
        minWidth: 120,
        cellRenderer: (params: any) => (
          <JobActionsCell
            data={params.data}
            onView={onView}
            onDelete={onDelete}
          />
        ),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
        },
      },
    ],
    [onView, onDelete]
  );
};

const jobListingColumnsDefs: ColDef<JobListingWithActions>[] = [
  {
    field: 'title',
    headerName: 'Job Title',
    sortable: true,
    filter: true,
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'openings',
    headerName: 'Openings',
    sortable: true,
    filter: false,
    minWidth: 100,
    suppressSizeToFit: true,
  },
  {
    field: 'skills',
    headerName: 'Skills',
    valueGetter: (params: any) => params.data?.skills?.join(', '),
    sortable: false,
    filter: true,
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'requirements',
    headerName: 'Requirements',
    valueGetter: (params: any) => params.data.requirements.join(' | '),
    sortable: false,
    filter: false,
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'whatYouBring',
    headerName: 'What You Bring',
    valueGetter: (params: any) => params.data.whatYouBring.join(' | '),
    sortable: false,
    filter: false,
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'perks',
    headerName: 'Perks',
    valueGetter: (params: any) => params.data.perks.join(' | '),
    sortable: false,
    filter: false,
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'createdAt',
    headerName: 'Posted On',
    valueGetter: (params: any) => {
      return new Date(params.data.createdAt);
    },
    sortable: true,
    filter: true,
    minWidth: 170,
    suppressSizeToFit: true,
  },
];
